import { createContext, ReactNode, useContext, useLayoutEffect } from 'react';

import { Alert } from '@mui/material';

import { ApplicationErrorModal } from 'components/modal/ApplicationErrorModal';
import { useLoaderContext } from 'context/LoaderContext';
import { useQueryPermissions } from 'hooks/permissions/useQueryPermissions';

type ArquivoKeys = 'ARQUIVO_VISUALIZAR' | 'ARQUIVO_CADASTRAR' | 'ARQUIVO_ALTERAR' | 'ARQUIVO_EXCLUIR' | 'ARQUIVO_MODERAR';
type ArquivoCategoriasKeys = 'ARQUIVO_CATEGORIA_GERENCIAR' | 'ARQUIVO_CATEGORIA_MODERAR';

type ComunicadoKeys =
    | 'COMUNICADO_VISUALIZAR'
    | 'COMUNICADO_CADASTRAR'
    | 'COMUNICADO_ALTERAR'
    | 'COMUNICADO_MODERAR'
    | 'COMUNICADO_EXCLUIR'
    | 'COMUNICADO_LOG_VISUALIZAR';
type ComunicadoCategoriasKeys = 'COMUNICADO_CATEGORIA_GERENCIAR' | 'COMUNICADO_CATEGORIA_MODERAR';

type CompraPedidoKeys = 'COMPRA_PEDIDO_VISUALIZAR' | 'COMPRA_PEDIDO_FINALIZAR' | 'COMPRA_PEDIDO_ACESSO_GERAL' | 'COMPRA_PEDIDO_FINALIZACAO_GERAL';
type CompraPedidoConfiguracaoKeys = 'COMPRA_PEDIDO_CONFIGURACAO_VISUALIZAR';
type CompraProdutoKeys = 'COMPRA_PRODUTO_VISUALIZAR' | 'COMPRA_PRODUTO_ALTERAR';
type CompraSugestaoKeys = 'COMPRA_SUGESTAO_VISUALIZAR';
type CompraPedidoLoteKeys = 'COMPRA_PEDIDO_LOTE_VISUALIZAR';
type CompraPedidoInstitucional = 'COMPRA_PEDIDO_ACESSA_LOJA_INSTITUCIONAL';
type CompraPedidoTotalizadorKeys = 'COMPRA_PEDIDO_TOTALIZADOR_VISUALIZAR';
type CompraPercentualNotaFiscalKeys = 'COMPRA_PERCENTUAL_NOTA_FISCAL_VISUALIZAR';

type CompraPedidoListagemKeys =
    | 'COMPRA_PEDIDO_LISTAGEM_VISUALIZAR'
    | 'COMPRA_PEDIDO_LISTAGEM_GRADE'
    | 'COMPRA_PEDIDO_LISTAGEM_SINCRONIZAR'
    | 'COMPRA_PEDIDO_LISTAGEM_CLONAR'
    | 'COMPRA_PEDIDO_LISTAGEM_EXCLUIR'
    | 'COMPRA_PEDIDO_LISTAGEM_APROVAR'
    | 'COMPRA_PEDIDO_LISTAGEM_EXCLUIR_TOTALIZADOR'
    | 'COMPRA_PEDIDO_ACESSAR_LOGS'
    | 'COMPRA_PEDIDO_COPIAR_DADOS_PEDIDOS'
    | 'COMPRA_PEDIDO_REATIVAR_PEDIDO_EXCLUIDO';

type BannerKeys = 'BANNER_VISUALIZAR' | 'BANNER_CADASTRAR' | 'BANNER_ALTERAR' | 'BANNER_MODERAR' | 'BANNER_EXCLUIR';

type MarketingPecasKeys =
    | 'MARKETING_PECA_VISUALIZAR'
    | 'MARKETING_PECA_CADASTRAR'
    | 'MARKETING_PECA_ALTERAR'
    | 'MARKETING_PECA_EXCLUIR'
    | 'MARKETING_PECA_MODERAR';
type MarketingCampanhasKeys = 'MARKETING_CAMPANHA_GERENCIAR' | 'MARKETING_CAMPANHA_MODERAR';

type TradePecasKeys = 'TRADE_PECA_VISUALIZAR' | 'TRADE_PECA_CADASTRAR' | 'TRADE_PECA_ALTERAR' | 'TRADE_PECA_EXCLUIR' | 'TRADE_PECA_MODERAR';
type TradeSolicitacoesKeys = 'TRADE_SOLICITACAO_PECA_VISUALIZAR' | 'TRADE_SOLICITACAO_PECA_SOLICITAR' | 'TRADE_SOLICITACAO_PECA_MODERAR';
type TradeCampanhasKeys = 'TRADE_CAMPANHA_GERENCIAR' | 'TRADE_CAMPANHA_MODERAR';

type CalendarioKeys = 'CALENDARIO_VISUALIZAR';
type CalendarioEventosKeys = 'CALENDARIO_EVENTO_PERSONALIZADO_GERENCIAR' | 'CALENDARIO_EVENTO_MARCADOR_GERENCIAR';

type UsuariosKeys = 'USUARIO_VISUALIZAR' | 'USUARIO_CADASTRAR' | 'USUARIO_ALTERAR' | 'USUARIO_MODERAR' | 'USUARIO_EXCLUIR';
type UsuariosGruposKey = 'USUARIO_GRUPO_RELACIONAR';

type GrupoEconomicoKeys =
    | 'GRUPO_ECONOMICO_VISUALIZAR'
    | 'GRUPO_ECONOMICO_CADASTRAR'
    | 'GRUPO_ECONOMICO_ALTERAR'
    | 'GRUPO_ECONOMICO_MODERAR'
    | 'GRUPO_ECONOMICO_EXCLUIR';

type GruposPermissoesKeys =
    | 'GRUPO_PERMISSAO_VISUALIZAR'
    | 'GRUPO_PERMISSAO_CADASTRAR'
    | 'GRUPO_PERMISSAO_ALTERAR'
    | 'GRUPO_PERMISSAO_MODERAR'
    | 'GRUPO_PERMISSAO_EXCLUIR';

type SecoesKeys = 'SECAO_GERENCIAR' | 'SECAO_DADOS_ALTERAR';

type FinanceiroRoyaltiesExtratoKeys = 'FINANCEIRO_EXTRATO_ROYALTIES_VISUALIZAR';
type FinanceiroRoyaltiesEspelhoKeys = 'FINANCEIRO_ESPELHO_ROYALTIES_VISUALIZAR';
type FinanceiroFppExtratoKeys = 'FINANCEIRO_EXTRATO_FPP_VISUALIZAR';
type FinanceiroFppEspelhoKeys = 'FINANCEIRO_ESPELHO_FPP_VISUALIZAR';
type FinanceiroTitulosKeys = 'FINANCEIRO_TITULO_VISUALIZAR' | 'FINANCEIRO_TITULO_BOLETO_GERAR' | 'FINANCEIRO_TITULO_EXPORTAR';

type FinanceiroCreditoDebitoKeys = 'FINANCEIRO_CREDITO_DEBITO_DETALHAR';
type FinanceiroCreditoKeys =
    | 'FINANCEIRO_DETALHAMENTO_CREDITO_VISUALIZAR'
    | 'FINANCEIRO_DETALHAMENTO_CREDITO_IMPORTAR'
    | 'FINANCEIRO_DETALHAMENTO_CREDITO_EXCLUIR';
type FinanceiroNotasKeys = 'FINANCEIRO_NOTA_FISCAL_VISUALIZAR' | 'FINANCEIRO_NOTA_FISCAL_DETALHAR' | 'FINANCEIRO_NOTA_FISCAL_EXPORTAR';

type FinanceiroDevolucaoKeys = 'FINANCEIRO_DEVOLUCAO_VISUALIZAR' | 'FINANCEIRO_DEVOLUCAO_SIMULAR' | 'FINANCEIRO_DEVOLUCAO_EXCLUIR';
type FinanceiroDevolucaoMotivoKeys = 'FINANCEIRO_DEVOLUCAO_MOTIVO_GERENCIAR';

type FinanceiroDreKeys =
    | 'FINANCEIRO_DRE_VISUALIZAR'
    | 'FINANCEIRO_DRE_PREENCHER'
    | 'FINANCEIRO_DRE_ENVIAR'
    | 'FINANCEIRO_DRE_CONTESTAR'
    | 'FINANCEIRO_DRE_ANALITICA_VISUALIZAR'
    | 'FINANCEIRO_DRE_SINTETICA_VISUALIZAR'
    | 'FINANCEIRO_DRE_MODERAR'
    | 'FINANCEIRO_DRE_RELATORIO_EXPORTAR'
    | 'FINANCEIRO_DRE_CONFIGURACAO';

type FinanceiroDrePreenchimentoKeys = 'FINANCEIRO_DRE_PREENCHIMENTO_GERENCIAR';

type FinanceiroRecebiveisKeys = 'FINANCEIRO_RECEBIVEIS_VISUALIZAR';

type FinanceiroReembolsoPromocoesKeys = 'FINANCEIRO_REEMBOLSO_CONSULTAR' | 'FINANCEIRO_REEMBOLSO_GERAR' | 'FINANCEIRO_REEMBOLSO_MANUTENCAO';

type BiPainelKeys =
    | 'BI_VENDA_VISUALIZAR'
    | 'BI_ESTOQUE_VISUALIZAR'
    | 'BI_DIGITAL_VISUALIZAR'
    | 'BI_SELLIN_VISUALIZAR'
    | 'BI_INICIO_VISUALIZAR'
    | 'BI_REVENDA_VISUALIZAR';

type LojaKeys = 'LOJA_GERENCIAR' | 'LOJA_MODERAR' | 'LOJA_VISUALIZAR' | 'LOJA_CADASTRAR' | 'LOJA_ALTERAR' | 'LOJA_EXCLUIR';

type TagKeys = 'TAG_GERENCIAR';

type EstatisticaKeys = 'ESTATISTICA_VISUALIZAR';
type GradeKeys = 'GRADE_VISUALIZAR' | 'GRADE_GERENCIAR';

type ComplementarCargo = 'COMPLEMENTAR_CARGO_GERENCIAR';
type ComplementarFrete = 'COMPLEMENTAR_FRETE_GERENCIAR';

type ComplementarGrupoEmails = 'COMPLEMENTAR_GRUPO_EMAIL_GERENCIAR';
type ComplementarCentroDistribuicao = 'COMPLEMENTAR_CENTRO_DISTRIBUICAO_GERENCIAR';
type ComplementarCentroCusto = 'COMPLEMENTAR_CENTRO_CUSTO_GERENCIAR';
type ComplementarCentroPedido = 'COMPLEMENTAR_CENTRO_PEDIDO_GERENCIAR';
type ComplementarCondicaoPagamento = 'COMPLEMENTAR_CONDICAO_PAGAMENTO_GERENCIAR';
type ComplementarDeposito = 'COMPLEMENTAR_DEPOSITO_GERENCIAR';
type ComplementarOperacao = 'COMPLEMENTAR_OPERACAO_GERENCIAR';
type ComplementarBandaPreco = 'COMPLEMENTAR_BANDA_PRECO_GERENCIAR';
type ComplementarLocalFuncionario = 'COMPLEMENTAR_LOCAL_FUNCIONARIO';
type ComplementarConfigBi = 'COMPLEMENTAR_CONFIG_BI';
type ComplementarCluster = 'COMPLEMENTAR_CLUSTER_LOJA_GERENCIAR';

type QuebrasKeys = 'QUEBRAS_VISUALIZAR' | 'QUEBRAS_IMPORTAR';

type RevendedorKeys = 'REVENDEDOR_CADASTRO_REVENDEDOR';
type RevendedorPromocaoKeys = 'REVENDEDOR_CADASTRO_PROMOCAO';
type RevendedorConfiguracaoGeralKeys = 'REVENDEDOR_CADASTRO_CONFIGURACOES';

type LogisticaSeller = 'LOGISTICA_SELLER_GERENCIAR';

export type PermissionKey =
    | ComplementarOperacao
    | ComplementarDeposito
    | ComplementarCondicaoPagamento
    | ComplementarCentroPedido
    | ComplementarCentroCusto
    | ComplementarCentroDistribuicao
    | ComplementarGrupoEmails
    | ComplementarCargo
    | ComplementarFrete
    | ComplementarBandaPreco
    | ComplementarLocalFuncionario
    | ComplementarConfigBi
    | CompraSugestaoKeys
    | CompraPedidoTotalizadorKeys
    | CompraPedidoLoteKeys
    | CompraProdutoKeys
    | CompraPedidoKeys
    | CompraPedidoConfiguracaoKeys
    | CompraPedidoListagemKeys
    | CompraPedidoInstitucional
    | CompraPercentualNotaFiscalKeys
    | ComunicadoKeys
    | ComunicadoCategoriasKeys
    | MarketingPecasKeys
    | MarketingCampanhasKeys
    | TradePecasKeys
    | TradeCampanhasKeys
    | TradeSolicitacoesKeys
    | ArquivoKeys
    | ArquivoCategoriasKeys
    | CalendarioKeys
    | CalendarioEventosKeys
    | UsuariosKeys
    | UsuariosGruposKey
    | FinanceiroRoyaltiesExtratoKeys
    | FinanceiroRoyaltiesEspelhoKeys
    | FinanceiroFppExtratoKeys
    | FinanceiroFppEspelhoKeys
    | FinanceiroNotasKeys
    | FinanceiroDevolucaoKeys
    | FinanceiroDevolucaoMotivoKeys
    | FinanceiroTitulosKeys
    | FinanceiroCreditoKeys
    | FinanceiroCreditoDebitoKeys
    | FinanceiroDreKeys
    | FinanceiroRecebiveisKeys
    | FinanceiroReembolsoPromocoesKeys
    | GrupoEconomicoKeys
    | GruposPermissoesKeys
    | SecoesKeys
    | BiPainelKeys
    | BannerKeys
    | FinanceiroDrePreenchimentoKeys
    | LojaKeys
    | LojaKeys
    | FinanceiroDrePreenchimentoKeys
    | TagKeys
    | EstatisticaKeys
    | GradeKeys
    | QuebrasKeys
    | RevendedorKeys
    | RevendedorPromocaoKeys
    | RevendedorConfiguracaoGeralKeys
    | ComplementarCluster
    | LogisticaSeller;

interface PermissionsContextData {
    hasPermission: (permissionsKeys: PermissionKey[]) => boolean;
    hasAnyPermission: (permissionsKeys: PermissionKey[]) => boolean;
    hasFormPermission: (module: string, paramId: string | undefined) => boolean;
    alertNotPermission: () => React.ReactNode;
}

interface PermissionsProviderProps {
    children?: ReactNode;
}

const PermissionsContext = createContext<PermissionsContextData>({} as PermissionsContextData);

const AlertNotPermission = () => {
    return (
        <Alert sx={{ m: 2 }} severity="error">
            Você não possui permissão
        </Alert>
    );
};

export function PermissionsProvider({ children }: PermissionsProviderProps) {
    const { setLoader } = useLoaderContext();
    const { data: permissions = [], isFetching, isError } = useQueryPermissions();

    /**
     * Verifica se o usuário possui todas as permissões passadas como parâmetro.
     */
    const hasPermission = (permissionsKeys: PermissionKey[]): boolean => {
        return permissionsKeys.every((permissionKey) => permissions.some((permission) => permissionKey === permission.dsChavePermissao));
    };

    /**
     * Verifica se o usuário possui alguma das permissões passadas como parâmetro.
     */
    const hasAnyPermission = (permissionsKeys: PermissionKey[]): boolean => {
        return permissionsKeys.some((permissionKey) => permissions.some((permission) => permissionKey === permission.dsChavePermissao));
    };

    /**
     * Verifica se o usuário possui permissão para acessar um formulário.
     */
    const hasFormPermission = (module: string, paramId: string | undefined) => {
        // filtra as permissões que se encaixam no módulo informado
        const permissionsMatches = permissions.filter((permission) => permission.dsChavePermissao.includes(module));

        // mapeia as permissões para um array de strings
        const permissionsArr = permissionsMatches.map((permission) => permission.dsChavePermissao);

        // se tiver um id e não tiver permissão de alterar ou gerenciar, retorna false
        if (paramId && !permissionsArr.some((permission) => permission.includes('ALTERAR') || permission.includes('GERENCIAR'))) return false;

        // verifica se tem alguma permissão de alterar, gerenciar, cadastrar, moderar ou solicitar
        return permissionsArr.some(
            (permission) =>
                permission.includes('GERENCIAR') ||
                permission.includes('ALTERAR') ||
                permission.includes('CADASTRAR') ||
                permission.includes('MODERAR') ||
                permission.includes('SOLICITAR'),
        );
    };

    /**
     * Desliga o loading geral quando todas as requisições estiverem prontas.
     */
    useLayoutEffect(() => {
        if (!isFetching) setLoader(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetching]);

    if (isFetching) return null;

    if (isError || !permissions.length) return <ApplicationErrorModal type="permissions" />;

    return (
        <PermissionsContext.Provider
            value={{
                hasPermission,
                hasFormPermission,
                hasAnyPermission,
                alertNotPermission: AlertNotPermission,
            }}
        >
            {children}
        </PermissionsContext.Provider>
    );
}

export const usePermissionsContext = (): PermissionsContextData => useContext(PermissionsContext);
